import React, { useEffect, useState } from "react";
import { Alert, Modal } from "antd";
import "./SiteBanner.scss";
import { useAppDispatch, useTypedSelector } from "../../../store/app.store.tsx";
import { FetchBanners } from "../../../store/banner.reducer.tsx";
import { Banner } from "../../../services/banner.service.ts";
import { UI_STRINGS } from "../../../constants/ui-strings.ts";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const WINDOW_CHECK_INTERVAL_MS = 30 * 1000;
const DISMISSED_STORAGE_KEY = "dismissedBanners";
const ACKNOWLEDGED_STORAGE_KEY = "acknowledgedBanners";

const readIds = (key: string): string[] => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
};

const writeIds = (key: string, ids: string[]) => {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch (error) {
    // A blocked or full localStorage only costs the choice on reload.
  }
};

// The frontend and backend deploy independently, so `display` may be missing
// entirely. Anything unrecognised is treated as an inline banner — never as a
// dialog, which would block the page.
const displayOf = (banner: Banner) =>
  banner.display === "modal" || banner.display === "both"
    ? banner.display
    : "banner";

const isWithinWindow = (banner: Banner, now: number) => {
  const start = banner.start_date ? Date.parse(banner.start_date) : NaN;
  if (!Number.isNaN(start) && start > now) {
    return false;
  }

  const end = banner.end_date ? Date.parse(banner.end_date) : NaN;
  if (!Number.isNaN(end) && end <= now) {
    return false;
  }

  return true;
};

export const SiteBanner = () => {
  const dispatch = useAppDispatch();
  const banners = useTypedSelector((state) => state.banners.items);
  const [dismissed, setDismissed] = useState<string[]>(() =>
    readIds(DISMISSED_STORAGE_KEY)
  );
  const [acknowledged, setAcknowledged] = useState<string[]>(() =>
    readIds(ACKNOWLEDGED_STORAGE_KEY)
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    dispatch(FetchBanners());
    const pollId = setInterval(() => dispatch(FetchBanners()), POLL_INTERVAL_MS);
    return () => clearInterval(pollId);
  }, [dispatch]);

  // The backend stops returning a banner once it expires, but a tab left open
  // between polls would keep showing it, so the window is re-checked locally.
  useEffect(() => {
    const tickId = setInterval(() => setNow(Date.now()), WINDOW_CHECK_INTERVAL_MS);
    return () => clearInterval(tickId);
  }, []);

  const active = banners.filter((banner) => isWithinWindow(banner, now));

  const strips = active.filter(
    (banner) => displayOf(banner) !== "modal" && !dismissed.includes(banner.id)
  );

  // Only one dialog at a time; the next unacknowledged one appears after this
  // is acknowledged.
  const dialog = active.find(
    (banner) => displayOf(banner) !== "banner" && !acknowledged.includes(banner.id)
  );

  const onDismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    writeIds(DISMISSED_STORAGE_KEY, next);
  };

  const onAcknowledge = (id: string) => {
    const next = [...acknowledged, id];
    setAcknowledged(next);
    writeIds(ACKNOWLEDGED_STORAGE_KEY, next);
  };

  if (!strips.length && !dialog) {
    return null;
  }

  return (
    <>
      {strips.length > 0 && (
        <div className="site-banner" role="status">
          {strips.map((banner) => (
            <Alert
              key={banner.id}
              banner
              showIcon
              type={banner.severity}
              message={banner.message}
              closable={banner.dismissible}
              onClose={() => onDismiss(banner.id)}
            />
          ))}
        </div>
      )}

      {dialog && (
        <Modal
          centered
          open={true}
          className="site-banner-modal"
          title={dialog.title || UI_STRINGS.BANNER.DEFAULT_MODAL_TITLE}
          okText={UI_STRINGS.BANNER.ACKNOWLEDGE_BUTTON}
          // The dialog exists to be acknowledged, so the escape hatches (close
          // icon, mask click, Esc) are all removed.
          closeIcon={false}
          maskClosable={false}
          keyboard={false}
          onOk={() => onAcknowledge(dialog.id)}
          footer={(_, { OkBtn }) => (
            <>
              <OkBtn />
            </>
          )}
        >
          <p>{dialog.message}</p>
        </Modal>
      )}
    </>
  );
};
